import { useTranslation } from "react-i18next";

import { TestProps } from "shared/types";

import Button from "features/components/Button";
import Chip from "features/components/Chip";
import ChipList from "features/ui/ChipList";
import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import { getFilterGroupStateTopLevelRowAttributes } from "features/ui/Filters/FilterBuilder/utils";
import FilterLabel from "features/ui/Filters/other/FilterLabel";
import { FilterOverviewFormat } from "features/ui/Filters/types";
import { getFilterKeyMapValues } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

const FIELD_NAMES_WITH_TRANSFORMATION_REQUIRED_STARTS_WITH = ["predictions."];
const MAX_VISIBLE_FILTERS = 2000;

/**
 * Renders a list of filters in format specified.
 *
 * @param filters - Filters to render
 * @param onFiltersReset - Callback to reset filters
 * @param tableSchema - Table schema to get field names from
 * @param removable - If filters are removable
 * @param hideClearAll - If clear all button should be hidden
 * @param showValuesIfMultiple - If multiple values should be shown. If false, we show 1 value & when multiple we provide a modal
 * @param className - Class name for the container
 * @param format - Format of the filter, "badge" | "label-inline"
 * @param noModalForMultipleValues - Do not show a modal even if multiple values are present
 */
interface Props {
  filters: FilterGroupState;
  onFiltersReset?: (fieldNames?: string[]) => void;
  tableSchema?: SchemaEntry[];
  removable?: boolean;
  hideClearAll?: boolean;
  showValuesIfMultiple?: boolean;
  className?: string;
  format?: FilterOverviewFormat;
  noModalForMultipleValues?: boolean;
  dense?: boolean;
}

const FiltersOverview = ({
  filters,
  onFiltersReset,
  tableSchema,
  removable = true,
  hideClearAll = false,
  showValuesIfMultiple = false,
  className = "inline-flex gap-x-3 gap-y-1 flex-wrap items-baseline",
  format = "badge",
  noModalForMultipleValues = false,
  testId = "filters-overview",
  dense = false,
}: Props & TestProps) => {
  const { t } = useTranslation("filters");
  // Where FiltersOverview is used, we should never really support anything other than top-level attributes.
  // If we need to support nested attributes, we should use FilterQueryPresentation component instead.
  const filterAttributes = getFilterGroupStateTopLevelRowAttributes(filters);

  const filtersKeyMap: Record<string, string> = getFilterKeyMapValues(
    tableSchema || []
  );

  if (filterAttributes.length === 0) return null;

  const showClearAll =
    filterAttributes.length > 1 && removable && !hideClearAll;

  // TODO https://viaduct-ai.atlassian.net/browse/ENG-5405: Filtering components are currently a mess. Rewrite them to separate value formatting logic (dates, ...)
  // from presenting logic (chip, inline, ...)
  const allFilterEntries = filterAttributes.sort().map((fieldName) => {
    // Hack to avoid showing ie. "predictions.<hash>.days.30.riskStatus" until the name changes to actual FM name ..
    // This is now needed because our keyNameMap comes in later due to failure modes being feature flagged.
    // - we don't wanna show "Predictions 23 1a he f2 ..." in the filter label for a split second there!
    if (
      FIELD_NAMES_WITH_TRANSFORMATION_REQUIRED_STARTS_WITH.filter((startText) =>
        fieldName.startsWith(startText)
      ).length > 0 &&
      !filtersKeyMap[fieldName]
    ) {
      return false;
    }

    const label = (
      <FilterLabel
        key={fieldName}
        filters={filters}
        fieldName={fieldName}
        keyNameMap={filtersKeyMap}
        showValuesIfMultiple={showValuesIfMultiple}
        format={format}
        noModalForMultipleValues={noModalForMultipleValues}
        tableSchema={tableSchema}
        dense={dense}
      />
    );

    if (format === "badge") {
      return (
        <Chip
          key={fieldName}
          label={label}
          onDelete={
            removable
              ? () => onFiltersReset && onFiltersReset([fieldName])
              : undefined
          }
          size="small"
          data-testid={`${fieldName}-filter`}
        />
      );
    }

    return label;
  });

  if (allFilterEntries.filter(Boolean).length === 0) return null;

  return (
    <div className={className} data-testid={testId}>
      {format === "badge" && (
        <ChipList
          title=""
          maxVisible={MAX_VISIBLE_FILTERS}
          values={allFilterEntries.filter(Boolean) as JSX.Element[]}
          defaultWrapper={false}
        />
      )}
      {format !== "badge" && allFilterEntries}
      {showClearAll && (
        <Button
          color="secondary"
          size="small"
          onClick={() => onFiltersReset && onFiltersReset(filterAttributes)}
          testId="clear-all-filters"
        >
          {t("clear_all")}
        </Button>
      )}
    </div>
  );
};

export default FiltersOverview;
